<template>
  <div class="tw-relative">
    <section class="tw-relative tw-my-8">
      <header class="tw-mb-4">
        <cl-heading :size="'h3'">
          {{ $t("security-change-password") }}
        </cl-heading>
        <cl-p>
          {{ $t("change-password") }}
        </cl-p>
      </header>
      <form class="tw-mb-4 tw-w-full" @submit.prevent>
        <div class="tw-grid tw-grid-cols-1 tw-gap-5 md:tw-grid-cols-2">
          <cl-form-group class="tw-col-span-1 tw-mb-6">
            <t-password-input
              :id="'password'"
              v-model:value="v$.form.password.$model"
              :name="'password'"
              :placeholder="$t('password-placeholder')"
              :state="
                !v$.form.password.$dirty ? null : !v$.form.password.$error
              "
              :data-test-id="'profile-security-new-password'"
            />
            <span
              v-if="v$.form.password.$error"
              class="tw-text-system-error-500"
              >{{ $t("password-validation") }}</span
            >
          </cl-form-group>
          <cl-form-group class="tw-col-span-1 tw-mb-6">
            <t-password-input
              :id="'confirm-password'"
              v-model:value="v$.form.confirm_password.$model"
              :name="'confirm-password'"
              :placeholder="$t('confirm-password-placeholder')"
              :data-test-id="'profile-security-cofirm-password'"
              :state="
                !v$.form.confirm_password.$dirty
                  ? null
                  : !v$.form.confirm_password.$error
              "
            />
            <span
              v-if="v$.form.confirm_password.$error"
              class="tw-text-system-error-500"
              >{{ $t("confirm-password-validation") }}</span
            >
          </cl-form-group>
        </div>
        <div class="tw-flex tw-justify-start md:tw-justify-end">
          <cl-form-group class="tw-mb-6">
            <cl-button
              :variant="'secondary'"
              :type="'submit'"
              :disabled="loading"
              data-test-id="submit-update-password"
              @on-click="onChangePasswordSubmit"
            >
              <cl-spinner
                :loading-text="$t('loading')"
                v-if="loading"
                :size="'small'"
                class="tw-mr-2"
              />
              <span>{{ $t("save-changes") }}</span>
            </cl-button>
          </cl-form-group>
        </div>
      </form>
    </section>

    <cl-hr />

    <section class="tw-relative tw-my-8">
      <header class="tw-mb-4">
        <cl-heading :size="'h3'">
          {{ $t("security-2fa") }}
        </cl-heading>
        <cl-p>
          {{ $t("2fa-description") }}
        </cl-p>
      </header>
      <form
        v-if="!twoFactorAuthEnabled"
        autocomplete="off"
        class="tw-mb-4 tw-w-full"
        @submit.prevent
      >
        <div class="tw-grid tw-grid-cols-1 tw-gap-5 md:tw-grid-cols-2">
          <div class="tw-col-span-1">
            <cl-form-group>
              <cl-form-label :label-for="''">
                {{ $t("qr-code") }}
              </cl-form-label>
              <div
                class="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-rounded tw-border tw-border-primary-grey-100 tw-bg-white tw-p-6 tw-shadow-inner tw-shadow-primary-grey-100"
              >
                <img :src="qrCodeImage" class="tw-h-[228px] tw-w-[228px]" />
              </div>
            </cl-form-group>
          </div>
          <div class="tw-col-span-1">
            <cl-form-group class="tw-mb-2">
              <cl-form-label :label-for="'otp-secret'">
                {{ $t("otp-secret") }}
              </cl-form-label>
              <cl-form-input
                :id="'otp-secret'"
                :name="'otp-secret'"
                :type="'text'"
                :value="otpSecret"
                :data-test-id="'profile-security-2fa-secret'"
                readonly
              />
            </cl-form-group>
            <cl-form-group class="tw-mb-2">
              <cl-form-label :label-for="'user'">
                {{ $t("otp-secret") }}
              </cl-form-label>
              <cl-form-input
                :id="'user'"
                :name="'user'"
                :type="'text'"
                :value="authUser.email"
                :data-test-id="'profile-security-2fa-user'"
                readonly
              />
            </cl-form-group>
            <cl-form-group class="tw-mb-2">
              <cl-form-label :label-for="'code'">
                {{ $t("2fa-code-label") }}
              </cl-form-label>
              <cl-form-input
                :id="'code'"
                v-model:value="v$.authCode.$model as unknown as string"
                :name="'code'"
                :type="'text'"
                :placeholder="$t('2fa-code-placeholder')"
                :state="!v$.authCode.$dirty ? null : !v$.authCode.$error"
                :data-test-id="'profile-security-2fa-code'"
              />
              <span v-if="v$.authCode.$error" class="tw-text-system-error-500">
                <span v-if="!v$.authCode.required.$response">
                  {{ $t("2fa-code-validation") }}
                </span>
                <span v-else>
                  {{ $t("2fa-incorrect-recovery-code") }}
                </span>
              </span>
            </cl-form-group>
          </div>
        </div>
        <div class="tw-flex tw-justify-start md:tw-justify-end">
          <cl-form-group>
            <cl-button
              :variant="'secondary'"
              :type="'submit'"
              :disabled="fetching"
              data-test-id="submit-enable-2fa"
              @on-click="submitEnable2fa"
            >
              <cl-spinner
                :loading-text="$t('loading')"
                v-if="fetching"
                :size="'small'"
                class="tw-mr-2"
              />
              <span>{{ $t("enable-2fa") }}</span>
            </cl-button>
          </cl-form-group>
        </div>
      </form>
      <form
        v-if="twoFactorAuthEnabled"
        autocomplete="off"
        class="tw-mb-4 tw-w-full"
        @submit.prevent
      >
        <cl-form-group class="tw-mb-2">
          <cl-form-label :label-for="'2fa-status'">
            {{ $t("2fa-status-label") }}
          </cl-form-label>
          <cl-form-input
            :id="'2fa-status'"
            :name="'2fa-status'"
            :type="'text'"
            :placeholder="$t('2fa-code-placeholder')"
            :value="$t('2fa-status-placeholder')"
            disabled
          />
        </cl-form-group>
        <div class="tw-flex tw-justify-start md:tw-justify-end">
          <cl-form-group class="tw-mb-2">
            <cl-button
              :variant="'secondary'"
              :type="'submit'"
              :disabled="fetching"
              data-test-id="submit-disable-2fa"
              @on-click="submitDisable2fa"
            >
              <cl-spinner
                :loading-text="$t('loading')"
                v-if="fetching"
                :size="'small'"
                class="tw-mr-2"
              />
              <span>{{ $t("disable-2fa") }}</span>
            </cl-button>
          </cl-form-group>
        </div>
      </form>
    </section>

    <section
      v-if="twoFactorAuthEnabled && backupCodes !== null"
      class="tw-relative tw-my-8"
    >
      <header class="tw-mb-4">
        <cl-heading :size="'h3'">
          {{ $t("recovery-codes") }}
        </cl-heading>
        <cl-p>
          {{ $t("store-2fa-codes") }}
        </cl-p>
      </header>
      <div class="tw-mb-4">
        <div
          class="tw-relative tw-rounded tw-border tw-border-primary-grey-50 tw-bg-white tw-p-4"
        >
          <cl-p v-for="(code, idx) in backupCodes" :key="idx">
            {{ code }}
          </cl-p>
        </div>
      </div>
      <div class="tw-mb-4 tw-flex tw-justify-start md:tw-justify-end">
        <cl-button
          :variant="'secondary'"
          :type="'button'"
          data-test-id="copy2fa-button"
          @on-click="copy2faCodes"
        >
          <span>{{ $t("copy") }}</span>
        </cl-button>
      </div>
      <div class="tw-mb-4">
        <cl-badge :variant="'danger'" class="tw-font-semibold">
          {{ $t("save-2fa-codes") }}
        </cl-badge>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted } from "vue";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import { useAuthenticationStore } from "@/store/authentication.store";
import { useUsersStore } from "@/store/users.store";
import { usePageMetaStore } from "@/store/pageMeta.store";
// @ts-ignore - qrcode module doesn't have proper types
import qrcode from "qrcode";
import { Authenticator } from "@otplib/core-async";
import { keyDecoder, keyEncoder } from "@otplib/plugin-base32-enc-dec";
import { createRandomBytes } from "@/utils/get-random-bytes";
import { useVuelidate } from "@vuelidate/core";
import { required, sameAs, minLength, maxLength } from "@vuelidate/validators";
import { STRONG_PASSWORD } from "@/constants";
import TPasswordInput from "@/components/TPasswordInput";
import { useToastStore } from "@/components/TToasts/toast.store";
import { useCopyToClipboard } from "@/composables/useCopyToClipboard";
import { Buffer } from "buffer";
window.Buffer = Buffer;

type PasswordForm = {
  password: string;
  confirm_password: string;
};

type AuthUser = {
  email: string;
  uuid: string;
  twoFactorAuthEnabled: boolean;
};

type RouteQuery = {
  id?: string;
};

const authenticator = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  createRandomBytes: createRandomBytes as unknown as any,
  keyDecoder,
  keyEncoder,
};

const route = useRoute();
const { t } = useI18n();
const authStore = useAuthenticationStore();
const pageMetaStore = usePageMetaStore();
const usersStore = useUsersStore();
const toastStore = useToastStore();

const loading = ref<boolean>(false);
const twoFactorAuthEnabled = ref<boolean>(false);
const fetching = ref<boolean>(false);
const qrCodeImage = ref<string | undefined>(undefined);
const otpSecret = ref<string>(
  new Authenticator(authenticator).generateSecret(20),
);
const authCode = ref<string>("");

const form = reactive<PasswordForm>({
  password: "",
  confirm_password: "",
});

const rules = computed(() => ({
  form: {
    password: {
      required,
      strongPassword: (password: string) => STRONG_PASSWORD.test(password),
    },
    confirm_password: {
      required,
      sameAsPassword: sameAs(form.password),
    },
  },
  authCode: {
    required,
    minLength: minLength(6),
    maxLength: maxLength(6),
  },
}));

const v$ = useVuelidate(rules, { form, authCode });

const authUser = computed((): AuthUser => authStore.authUser as AuthUser);
const backupCodes = computed(() => authStore.backupCodes);

const setPageTitle = (payload: { title: string }) => {
  pageMetaStore.setPageTitle(payload);
};

const enable2fa = (payload: { secret: string; code: string; id?: string }) => {
  return authStore.enable2fa(
    payload as Parameters<typeof authStore.enable2fa>[0],
  );
};

const disable2fa = (uuid: string) => {
  return authStore.disable2fa(uuid);
};

const updateUser = (payload: { uuid?: string; formData: PasswordForm }) => {
  return usersStore.updateUserPassword(payload);
};

const generateQRCode = (): void => {
  const otpauth = new Authenticator(authenticator).keyuri(
    authUser.value.email || "Phish Titan User",
    "PHISH TITAN",
    otpSecret.value,
  );

  qrcode.toDataURL(otpauth, (err: Error | null, imageUrl: string) => {
    if (err) {
      return;
    }
    qrCodeImage.value = imageUrl;
  });
};

const { copy } = useCopyToClipboard();
const copy2faCodes = () => copy(backupCodes.value as string);

const submitEnable2fa = async (): Promise<void> => {
  const isValid = await v$.value.authCode.$validate();

  if (!isValid) {
    return;
  }

  try {
    fetching.value = true;

    await enable2fa({
      secret: otpSecret.value,
      code: authCode.value,
      id: (route.query as RouteQuery).id,
    });

    toastStore.displayToast({
      title: t("success"),
      message: t("updated"),
      duration: 2000,
      variant: "success",
      testId: "profile-security-update-toast-success",
    });

    twoFactorAuthEnabled.value = true;
  } catch {
    toastStore.displayToast({
      title: t("error"),
      message: t("2fa-invalid-code"),
      duration: 5000,
      variant: "danger",
      testId: "profile-security-update-toast-error",
    });
  } finally {
    fetching.value = false;
  }
};

const submitDisable2fa = async (): Promise<void> => {
  try {
    fetching.value = true;

    await disable2fa(authUser.value.uuid);

    toastStore.displayToast({
      title: t("success"),
      message: t("disabled"),
      duration: 2000,
      variant: "success",
      testId: "profile-security-disable-2fa-toast-success",
    });

    twoFactorAuthEnabled.value = false;
    generateQRCode();
  } finally {
    fetching.value = false;
  }
};

const onChangePasswordSubmit = async (): Promise<void> => {
  const isValid = await v$.value.form.$validate();

  if (!isValid) {
    return;
  }

  try {
    loading.value = true;

    await updateUser({
      uuid: (route.query as RouteQuery).id,
      formData: form,
    });

    toastStore.displayToast({
      title: t("success"),
      message: t("password-successfully-updated"),
      duration: 2000,
      variant: "success",
      testId: "profile-security-password-update-toast-success",
    });
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  setPageTitle({ title: t("security") });

  twoFactorAuthEnabled.value = authUser.value.twoFactorAuthEnabled;

  if (!twoFactorAuthEnabled.value) {
    generateQRCode();
  }
});
</script>
